/**
 * HTML 가상 패드. Phaser 입력과 겹치지 않게 캔버스 위 DOM 으로 둔다.
 * 홀드(방향)는 Controls 가 매 프레임 읽고, 단발·창 토글은 이벤트다.
 */

import { wantsTouchPad } from './runtime';

export type TouchHold = 'left' | 'right' | 'up' | 'down';
export type TouchShot =
  | 'jump'
  | 'attack'
  | 'skill1'
  | 'skill2'
  | 'skill3'
  | 'loot'
  | 'up'
  | 'down';

export type TouchUi =
  | 'start'
  | 'create'
  | 'login'
  | 'inventory'
  | 'stats'
  | 'skills'
  | 'gear'
  | 'quests'
  | 'titles'
  | 'help'
  | 'close'
  | 'mute'
  | 'hp'
  | 'mp'
  | 'chat'
  | 'revive-here'
  | 'revive-town';

const HOLD: TouchHold[] = ['left', 'right', 'up', 'down'];

class TouchPadState {
  readonly held = new Set<TouchHold>();
  readonly shots = new Set<TouchShot>();

  consumeShots(): ReadonlySet<TouchShot> {
    const copy = new Set(this.shots);
    this.shots.clear();
    return copy;
  }
}

export const touchPad = new TouchPadState();

export const TOUCH_EVENT = 'maple-touch';

export function mountTouchPad(): void {
  const root = document.getElementById('touch-pad');
  if (!root) return;
  if (!wantsTouchPad()) {
    root.hidden = true;
    return;
  }
  document.documentElement.dataset.touch = '1';
  root.hidden = false;
  if (import.meta.env.DEV) {
    (window as unknown as { __touchPad: typeof touchPad }).__touchPad = touchPad;
  }

  const setHold = (name: TouchHold, on: boolean): void => {
    if (on) touchPad.held.add(name);
    else touchPad.held.delete(name);
  };

  for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-hold]'))) {
    const name = btn.dataset.hold as TouchHold;
    if (!HOLD.includes(name)) continue;
    const down = (ev: PointerEvent): void => {
      ev.preventDefault();
      setHold(name, true);
      if (name === 'up') touchPad.shots.add('up');
      if (name === 'down') touchPad.shots.add('down');
      try {
        btn.setPointerCapture(ev.pointerId);
      } catch {
        /* 합성 이벤트·일부 웹뷰는 capture 를 거부한다 */
      }
    };
    const up = (ev: PointerEvent): void => {
      ev.preventDefault();
      setHold(name, false);
    };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', up);
  }

  for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-shot]'))) {
    const name = btn.dataset.shot as TouchShot;
    btn.addEventListener('pointerdown', (ev: PointerEvent) => {
      ev.preventDefault();
      touchPad.shots.add(name);
    });
  }

  for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>('button[data-ui]'))) {
    const ui = btn.dataset.ui as TouchUi;
    btn.addEventListener('pointerdown', (ev: PointerEvent) => {
      ev.preventDefault();
      window.dispatchEvent(new CustomEvent(TOUCH_EVENT, { detail: ui }));
    });
  }
}
